import { useState } from "react";
import { DetailRow } from "./DetailRow";
import Header from "./Header";
import { StatusDisplay } from "./StatusDisplay";
import {
  Container,
  UpperContainer,
  KeyDetailsContainer,
  Divider,
  ShowMoreButton,
} from "./LeftPane.styles";
import {
  StatusCircleEmpty,
  LightningIcon,
  EmailIcon,
  PhoneIcon,
  CalendarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BankIcon,
} from "src/assets/icons";
import Dropdown from "src/components/Dropdown";
import { timeAgo } from "src/utils";
import CalendarRow from "./CalendarRow";
import TeamDetailsRow from "./TeamDetailsRow";
import { useApplication } from "../ApplicationContext";
import { useLenderOperations } from "src/hooks/useLenderOperations";
import ListMenu from "./ListMenu";

interface LeftPaneProps {
  currentSection: string;
}

export const LeftPane = ({ currentSection }: LeftPaneProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { application, updateApplicationDetails, refetchApplication } =
    useApplication();
  const { setPrimaryLender } = useLenderOperations(
    application,
    refetchApplication,
  );
  const {
    borrower,
    co_borrowers,
    target_delivery_date,
    target_funding_date,
    application_status,
    lenders,
    primary_lender,
    last_updated_at,
    application_date,
    team_members,
    sales_rep,
    loan_officer,
  } = application;

  const lenderSelectionObject = lenders
    ?.filter((lender) => lender.enabled && !!lender.app_status)
    .reduce<Record<string, string>>((acc, curr) => {
      if (!curr.id || !curr.name) return acc;
      return { ...acc, [curr.id]: curr.name };
    }, {});
  const name = borrower?.first_name + " " + borrower?.last_name;
  const shouldShowTeamDetails = Boolean(
    sales_rep?.name ||
    sales_rep?.email ||
    (team_members?.length ?? 0) > 0 ||
    loan_officer?.name ||
    loan_officer?.phone_number,
  );

  return (
    <Container>
      <UpperContainer>
        <KeyDetailsContainer>
          <Header
            name={name}
            applicationId={application.application_id ?? ""}
          />
          {isExpanded && (
            <>
              <DetailRow
                icon={<PhoneIcon />}
                keyText="Phone"
                value={borrower?.phone_number}
                borderType="dashed"
              />
              <DetailRow
                icon={<EmailIcon />}
                keyText="Email"
                value={borrower?.email}
              />
            </>
          )}
          <DetailRow
            icon={<BankIcon />}
            keyText="Lender"
            value={
              lenderSelectionObject && (
                <Dropdown
                  menuItemsObj={lenderSelectionObject}
                  selectedItem={primary_lender ?? ""}
                  onSelectedItemChange={(value: string) => {
                    void setPrimaryLender(value);
                  }}
                />
              )
            }
            allowOverflow={true}
          />
          <DetailRow
            icon={<StatusCircleEmpty />}
            keyText="Status"
            value={
              <StatusDisplay
                applicationStatus={application_status ?? ""}
                isPorchpassLender={primary_lender === "porchpass"}
                updateApplicationDetails={updateApplicationDetails}
                refetchApplication={refetchApplication}
              />
            }
            allowOverflow={true}
          />
          <DetailRow
            icon={<LightningIcon />}
            keyText="Last Updated"
            value={timeAgo(last_updated_at ?? "")}
          />
          {isExpanded && (
            <>
              <DetailRow
                icon={<CalendarIcon />}
                keyText="Application Date"
                value={timeAgo(application_date ?? "")}
              />
              <Divider />
              {(co_borrowers?.length ?? 0) > 0 && co_borrowers?.[0] && (
                <>
                  <DetailRow
                    keyText="Co-borrower"
                    value={
                      co_borrowers[0].first_name +
                      " " +
                      co_borrowers[0].last_name
                    }
                    borderType="dashed"
                  />
                  <DetailRow
                    icon={<PhoneIcon />}
                    keyText="Phone"
                    value={co_borrowers[0].phone_number}
                    borderType="dashed"
                  />
                  <DetailRow
                    icon={<EmailIcon />}
                    keyText="Email"
                    value={co_borrowers[0].email}
                  />
                </>
              )}
            </>
          )}
          <CalendarRow
            colorStyle="blue"
            labelText="Target delivery date"
            date={target_delivery_date ?? null}
          />
          <CalendarRow
            colorStyle="tan"
            labelText="Target funding date"
            date={target_funding_date ?? null}
            borderType="none"
          />
          <Divider />
          <ShowMoreButton onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
            Show {isExpanded ? "less" : "more"} details
          </ShowMoreButton>
        </KeyDetailsContainer>
        <ListMenu currentSection={currentSection} />
      </UpperContainer>
      {shouldShowTeamDetails && (
        <TeamDetailsRow
          salesRep={sales_rep ?? {}}
          teamMembers={team_members ?? []}
          loanOfficer={loan_officer ?? {}}
        />
      )}
    </Container>
  );
};
